// Tipos de acao dos botoes CTA
export type ButtonActionType = 'QUICK_REPLY' | 'VISITAR_SITE';

// Categoria dos botoes
export type ButtonCategory = 'NONE' | 'QUICK_REPLY' | 'CTA';

// Tipo de URL
export type ButtonUrlType = 'STATIC' | 'DYNAMIC';

// Estrutura de um botao CTA do template
export interface TemplateButton {
  id: string;
  actionType: ButtonActionType;
  text: string;           // max 25 chars
  value?: string;         // URL (opcional para Quick Reply)
  urlType?: ButtonUrlType;
  linkKey?: string;       // Key do link do sistema (ex: 'link_rastreamento')
}

// Links disponíveis para botões de URL
export interface ButtonLinkOption {
  key: string;
  label: string;
  icon: string;
  description: string;
}

export const BUTTON_LINK_OPTIONS: ButtonLinkOption[] = [
  {
    key: 'link_rastreamento',
    label: 'Link Rastreamento',
    icon: 'fa-map-marker-alt',
    description: 'Ver status do pedido'
  },
  {
    key: 'link_cartao',
    label: 'Link Cartão',
    icon: 'fa-id-card',
    description: 'Acessar cartão fidelidade'
  },
  {
    key: 'link_cardapio',
    label: 'Link Cardápio',
    icon: 'fa-utensils',
    description: 'Acessar cardápio'
  },
  {
    key: 'link_avaliar',
    label: 'Link Avaliar',
    icon: 'fa-star',
    description: 'Avaliar pedido'
  },
  {
    key: 'link_pedido',
    label: 'Link Pedido',
    icon: 'fa-shopping-bag',
    description: 'Ver/pagar pedido'
  },
  {
    key: 'link_cupom',
    label: 'Link Cupom',
    icon: 'fa-ticket-alt',
    description: 'Resgatar cupom'
  },
  {
    key: 'link_ativar_cartao',
    label: 'Link Ativar Cartão',
    icon: 'fa-check-circle',
    description: 'Ativar cartão novo'
  },
  {
    key: 'link_extrato_cartao',
    label: 'Link Extrato',
    icon: 'fa-file-invoice',
    description: 'Ver extrato do cartão'
  }
];

// Categorias de variaveis para agrupamento
export type VariableCategory = 'cliente' | 'empresa' | 'pedido' | 'fidelidade' | 'links';

// Variavel de template para simulacao
export interface TemplateVariable {
  key: string;            // ex: nome_do_cliente
  label: string;          // ex: Nome do Cliente
  placeholder: string;    // ex: {{nome_do_cliente}}
  simulationValue: string;
  icon: string;           // classe FontAwesome
  color?: 'green' | 'blue' | 'orange' | 'purple'; // Cor do destaque
  category: VariableCategory; // Categoria para agrupamento no dropdown
}

// Estrutura principal do template para edicao
export interface WhatsAppTemplateEdit {
  id?: number;
  nomeTemplate: string;
  tipoNotificacao: string;
  status: string;
  categoria: 'UTILITY' | 'MARKETING';
  headerText: string;
  bodyText: string;
  footerText: string;
  buttonCategory: ButtonCategory;
  buttons: TemplateButton[];
  dataUltimaAlteracao?: string;
}

// Dados passados ao abrir o modal
export interface TemplateEditorData {
  template: WhatsAppTemplateEdit;
  mode: 'create' | 'edit';
}

// Resultado retornado ao fechar o modal
export interface TemplateEditorResult {
  saved: boolean;
  template?: WhatsAppTemplateEdit;
}

// Variaveis pre-definidas disponiveis para insercao
export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  // === Cliente ===
  {
    key: 'nome_do_cliente',
    label: 'Nome Cliente',
    placeholder: '{{nome_do_cliente}}',
    simulationValue: 'Maria Silva',
    icon: 'fa-user',
    color: 'green',
    category: 'cliente'
  },
  {
    key: 'nome_contato',
    label: 'Primeiro Nome',
    placeholder: '{{nome_contato}}',
    simulationValue: 'Maria',
    icon: 'fa-user-circle',
    color: 'green',
    category: 'cliente'
  },
  {
    key: 'telefone_do_contato',
    label: 'Tel. Contato',
    placeholder: '{{telefone_do_contato}}',
    simulationValue: '(11) 99999-0000',
    icon: 'fa-phone',
    color: 'blue',
    category: 'cliente'
  },
  // === Empresa ===
  {
    key: 'empresa',
    label: 'Empresa',
    placeholder: '{{empresa}}',
    simulationValue: 'Burger King',
    icon: 'fa-building',
    color: 'orange',
    category: 'empresa'
  },
  {
    key: 'horario_funcionamento',
    label: 'Horário Func.',
    placeholder: '{{horario_funcionamento}}',
    simulationValue: 'Seg-Sex: 08h-22h',
    icon: 'fa-clock',
    color: 'orange',
    category: 'empresa'
  },
  // === Pedido ===
  {
    key: 'codigo_pedido',
    label: 'Código Pedido',
    placeholder: '{{codigo_pedido}}',
    simulationValue: '#5492',
    icon: 'fa-tag',
    color: 'orange',
    category: 'pedido'
  },
  {
    key: 'status_pedido',
    label: 'Status Pedido',
    placeholder: '{{status_pedido}}',
    simulationValue: 'Em preparação',
    icon: 'fa-info-circle',
    color: 'blue',
    category: 'pedido'
  },
  {
    key: 'dados_pedido',
    label: 'Dados Pedido',
    placeholder: '{{dados_pedido}}',
    simulationValue: '2x Burger, 1x Refri',
    icon: 'fa-list',
    color: 'blue',
    category: 'pedido'
  },
  {
    key: 'tempo_de_entrega',
    label: 'Tempo Entrega',
    placeholder: '{{tempo_de_entrega}}',
    simulationValue: '30-40 min',
    icon: 'fa-clock',
    color: 'orange',
    category: 'pedido'
  },
  // === Fidelidade ===
  {
    key: 'saldo',
    label: 'Saldo',
    placeholder: '{{saldo}}',
    simulationValue: 'R$ 50,00',
    icon: 'fa-dollar-sign',
    color: 'green',
    category: 'fidelidade'
  },
  {
    key: 'pontos_expirar',
    label: 'Pontos a Expirar',
    placeholder: '{{pontos_expirar}}',
    simulationValue: '150 pontos',
    icon: 'fa-exclamation-triangle',
    color: 'orange',
    category: 'fidelidade'
  },
  {
    key: 'tempo_restante',
    label: 'Tempo Restante',
    placeholder: '{{tempo_restante}}',
    simulationValue: '7 dias',
    icon: 'fa-hourglass-half',
    color: 'orange',
    category: 'fidelidade'
  },
  // === Links ===
  {
    key: 'link_rastreamento',
    label: 'Link Rastreio',
    placeholder: '{{link_rastreamento}}',
    simulationValue: 'https://demo.meucardapio.ai/ver-pedido/...',
    icon: 'fa-map-marker-alt',
    color: 'purple',
    category: 'links'
  },
  {
    key: 'link_avaliar',
    label: 'Link Avaliar',
    placeholder: '{{link_avaliar}}',
    simulationValue: 'https://demo.meucardapio.ai/avaliar-pedido/...',
    icon: 'fa-star',
    color: 'purple',
    category: 'links'
  },
  {
    key: 'link_pedido',
    label: 'Link Pedido',
    placeholder: '{{link_pedido}}',
    simulationValue: 'https://demo.meucardapio.ai/pedido/...',
    icon: 'fa-shopping-bag',
    color: 'purple',
    category: 'links'
  },
  {
    key: 'link_cardapio',
    label: 'Link Cardápio',
    placeholder: '{{link_cardapio}}',
    simulationValue: 'https://demo.meucardapio.ai',
    icon: 'fa-utensils',
    color: 'purple',
    category: 'links'
  },
  {
    key: 'link_cartao',
    label: 'Link Cartão',
    placeholder: '{{link_cartao}}',
    simulationValue: 'https://demo.meucardapio.ai/cliente',
    icon: 'fa-id-card',
    color: 'purple',
    category: 'links'
  },
  {
    key: 'link_cupom',
    label: 'Link Cupom',
    placeholder: '{{link_cupom}}',
    simulationValue: 'https://demo.meucardapio.ai/cupom/...',
    icon: 'fa-ticket-alt',
    color: 'purple',
    category: 'links'
  },
  {
    key: 'link_ativar_cartao',
    label: 'Link Ativar',
    placeholder: '{{link_ativar_cartao}}',
    simulationValue: 'https://demo.meucardapio.ai/confirmar/...',
    icon: 'fa-check-circle',
    color: 'purple',
    category: 'links'
  },
  {
    key: 'link_desinscrever',
    label: 'Link Desinscrever',
    placeholder: '{{link_desinscrever}}',
    simulationValue: 'https://demo.meucardapio.ai/desinscrever/...',
    icon: 'fa-user-minus',
    color: 'purple',
    category: 'links'
  }
];
